import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { NumField, CurrencySelect, Money, currencySymbol, moneyText, num } from "./shared";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";
import { reportTable } from "@/lib/calc/report";
import { planBreakEven, breakEvenSteps, breakEvenTable, type BreakEvenRow } from "./breakEvenPlan";
import type { TableColumn } from "@/lib/calc/table";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));

const TOOL_INFO_CONTENT = {
  what: `Calculates exactly how many units you need to sell before covering your fixed costs and actually turning a profit, based on your price, variable cost per unit, and fixed monthly costs.`,
  how: `Enter your fixed costs, price per unit, and variable cost per unit, and your break-even point in units and revenue is calculated.`,
  tip: `Run this before finalizing a price, not after — it's much easier to adjust a price on paper before launch than to explain a price change to customers once you're already selling.`,
};

export default function BreakEvenCalculator() {
  const [currency, setCurrency] = useState("USD");
  const [fixedCosts, setFixedCosts] = useState("5000");
  const [pricePerUnit, setPricePerUnit] = useState("25");
  const [variableCostPerUnit, setVariableCostPerUnit] = useState("10");
  const [targetProfit, setTargetProfit] = useState("");
  const [expectedUnits, setExpectedUnits] = useState("");
  const [startupCost, setStartupCost] = useState("");
  const sym = currencySymbol(currency);

  const input = useMemo(() => ({
    fixedCosts: num(fixedCosts), price: num(pricePerUnit), variableCost: num(variableCostPerUnit),
    targetProfit: num(targetProfit, NaN), expectedUnits: num(expectedUnits, NaN), startupCost: num(startupCost, NaN),
  }), [fixedCosts, pricePerUnit, variableCostPerUnit, targetProfit, expectedUnits, startupCost]);
  const result = useMemo(() => planBreakEven(input), [input]);
  const fmt = (n: number, d = 2) => moneyText(n, currency, d);
  const steps = useMemo(() => (result.state === "ok" ? breakEvenSteps(input, result.plan, (n) => moneyText(n, currency, 2)) : []), [input, result, currency]);
  const rows = useMemo(() => (result.state === "ok" ? breakEvenTable(input, result.plan) : []), [input, result]);
  const columns: TableColumn<BreakEvenRow>[] = useMemo(() => [
    { key: "label", header: "Sales level", get: (r) => r.label },
    { key: "units", header: "Units", get: (r) => r.units, format: (r) => r.units.toLocaleString(), align: "right" },
    { key: "revenue", header: "Revenue", get: (r) => r.revenue, format: (r) => moneyText(r.revenue, currency), align: "right" },
    { key: "cost", header: "Total cost", get: (r) => r.totalCost, format: (r) => moneyText(r.totalCost, currency), align: "right" },
    { key: "profit", header: "Profit / loss", get: (r) => r.profit, format: (r) => moneyText(r.profit, currency), align: "right" },
  ], [currency]);

  const { blocked, gate } = useLogToolUsage(true, 3000);


  return (
    <ToolContainer>
      <div className="flex justify-end mb-3"><CurrencySelect value={currency} onChange={setCurrency} /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NumField label={`Fixed Costs (${sym}/month)`} value={fixedCosts} onChange={setFixedCosts} hint="Rent, salaries, subscriptions — costs that don't change with sales volume" />
        <NumField label={`Price per Unit (${sym})`} value={pricePerUnit} onChange={setPricePerUnit} />
        <NumField label={`Variable Cost per Unit (${sym})`} value={variableCostPerUnit} onChange={setVariableCostPerUnit} hint="Materials, packaging, per-unit fees" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <NumField label={`Target profit (${sym}/month, optional)`} value={targetProfit} onChange={setTargetProfit} hint="Leave empty for plain break-even, or enter a profit goal to see the units needed to reach it" />
        <NumField label="Expected units sold per month (optional)" value={expectedUnits} onChange={setExpectedUnits} hint="Adds the break-even price at this volume, your margin of safety and monthly profit" />
        <NumField label={`One-off start-up cost (${sym}, optional)`} value={startupCost} onChange={setStartupCost} hint="Needs the expected units: shows how many months of profit it takes to recover it" />
      </div>

      {blocked ? (
        <p className="text-sm text-center text-muted-foreground mt-6">— blocked at today's usage limit —</p>
      ) : result.state === "ok" ? ((() => { const p = result.plan, e = p.expected; return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card className="p-6 text-center">
            <p className="text-3xl font-bold text-primary" data-testid="be-units">{p.units.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground mt-1">Units to break even</p>
            <p className="text-xs text-muted-foreground mt-1">{p.exactUnits.toFixed(2)} rounded up: you cannot sell part of a unit</p>
          </Card>
          <Card className="p-6 text-center">
            <p className="text-3xl font-bold text-primary" data-testid="be-revenue"><Money n={p.revenueAtUnits} currency={currency} decimals={0} /></p>
            <p className="text-sm text-muted-foreground mt-1">Revenue for {p.units.toLocaleString()} units</p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="be-exact-revenue">Exact break-even revenue: {fmt(p.exactRevenue)}</p>
          </Card>
          <Card className="p-6 text-center">
            <p className="text-3xl font-bold text-primary">{p.contributionPct.toFixed(1)}%</p>
            <p className="text-sm text-muted-foreground mt-1">Contribution margin</p>
            <p className="text-xs text-muted-foreground mt-1">{fmt(p.contribution)} per unit</p>
          </Card>
          {p.target && (
            <Card className="p-6 text-center md:col-span-3" data-testid="be-target">
              <p className="text-2xl font-bold text-primary">{p.target.units.toLocaleString()} units</p>
              <p className="text-sm text-muted-foreground mt-1">
                to earn <Money n={p.target.profit} currency={currency} decimals={0} /> profit, with revenue of <Money n={p.target.revenue} currency={currency} decimals={0} />
              </p>
            </Card>
          )}
        </div>
        {e && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-testid="be-plan">
            <Card className="p-5 text-center">
              <p className="text-2xl font-bold text-primary" data-testid="be-price">{fmt(e.breakEvenPrice)}</p>
              <p className="text-sm text-muted-foreground mt-1">Break-even price at {e.units.toLocaleString()} units a month</p>
              <p className="text-xs text-muted-foreground mt-1">{e.breakEvenPrice > num(pricePerUnit) ? "Higher than your price: at this volume you lose money." : "At or below your price."}</p>
            </Card>
            <Card className="p-5 text-center">
              <p className={`text-2xl font-bold ${e.marginOfSafetyUnits < 0 ? "text-red-600" : "text-primary"}`} data-testid="be-safety">{e.marginOfSafetyUnits < 0 ? "−" : ""}{Math.abs(e.marginOfSafetyUnits).toLocaleString()} units ({Math.abs(e.marginOfSafetyPct).toFixed(1)}%)</p>
              <p className="text-sm text-muted-foreground mt-1">{e.marginOfSafetyUnits < 0 ? "Short of break-even" : "Margin of safety"}</p>
              <p className="text-xs text-muted-foreground mt-1">Sales can fall this far before you stop covering costs.</p>
            </Card>
            <Card className="p-5 text-center">
              <p className="text-2xl font-bold" data-testid="be-monthly"><Money n={e.monthlyProfit} currency={currency} /></p>
              <p className="text-sm text-muted-foreground mt-1">Profit per month at your price</p>
              {e.startup && <p className="text-xs mt-1" data-testid="be-recover">{e.startup.months === null ? "Start-up cost is never recovered at this volume and price." : `Start-up cost of ${fmt(e.startup.cost, 0)} recovered in ${e.startup.months.toFixed(1)} months.`}</p>}
            </Card>
          </div>
        )}
        <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
          <CalcReport
            title="Break-even analysis" tool="break-even-calculator" gate={gate} filename="break-even.csv" steps={steps}
            summary={`Break-even ${p.units} units (${fmt(p.revenueAtUnits, 0)} for those units; exact ${fmt(p.exactRevenue)})`}
            notes={["Cost-volume-profit arithmetic with your own figures. Illustration only, not financial advice."]}
            tables={[reportTable({ caption: "Break-even by sales level", heading: "Profit by sales level", rows, columns, rowKey: (x) => x.label })]}
          />
        </Suspense>
        </>
      ); })()) : result.state === "bad" ? (
        <NoticeBox className="text-sm text-center mt-6">Enter valid numbers (zero or more) in the first three boxes to see the break-even point.</NoticeBox>
      ) : (
        <Card className="mt-6 p-6 text-center border-red-200">
          <p className="text-red-600 font-medium">
            Your price per unit needs to be higher than your variable cost per
            unit — otherwise you lose money on every sale, no matter how many
            you sell.
          </p>
        </Card>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
